import { supabase } from './supabase/client';
import { v4 as uuidv4 } from 'uuid';
import { Transaction } from '@flows.cash/types';

export interface TransferTransaction {
  id: string; // transfer_id that links both transactions
  fromAccountId: string;
  toAccountId: string;
  amount: number; // Original amount in source currency
  exchangeRate: number;
  date: string;
  description: string;
  notes?: string;
  status: 'pending' | 'completed' | 'cancelled';
  tags?: string[];
  fromTransaction?: Transaction; // Source transaction
  toTransaction?: Transaction; // Destination transaction
  fromCurrency: string;
  toCurrency: string;
  convertedAmount: number; // Calculated amount in destination currency
  created_at: string;
  updated_at: string;
}

export interface CreateTransferRequest {
  fromAccountId: string;
  toAccountId: string;
  amount: number;
  exchangeRate?: number;
  date: string;
  description?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateTransferRequest {
  amount?: number;
  exchangeRate?: number;
  date?: string;
  description?: string;
  notes?: string;
  tags?: string[];
}

export class TransferService {
  /**
   * Create a new transfer transaction
   */
  async createTransfer(userId: string, request: CreateTransferRequest): Promise<TransferTransaction> {
    try {
      // Generate a unique transfer ID to link both transactions
      const transferId = uuidv4();
      const now = new Date().toISOString();

      // Get account information for currency and names
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, name, currency')
        .in('id', [request.fromAccountId, request.toAccountId])
        .eq('user_id', userId);

      if (!accounts || accounts.length !== 2) {
        throw new Error('Invalid account IDs provided');
      }

      const fromAccount = accounts.find(a => a.id === request.fromAccountId);
      const toAccount = accounts.find(a => a.id === request.toAccountId);

      if (!fromAccount || !toAccount) {
        throw new Error('Could not find specified accounts');
      }

      // Use provided exchange rate or default to 1.0 if same currency
      const exchangeRate = request.exchangeRate || 
        (fromAccount.currency === toAccount.currency ? 1.0 : await this.getCurrentExchangeRate(fromAccount.currency, toAccount.currency));

      // Calculate converted amount
      const convertedAmount = request.amount * exchangeRate;

      // Create outgoing transaction (expense)
      const outgoingTransaction: Partial<Transaction> = {
        id: uuidv4(),
        user_id: userId,
        account_id: request.fromAccountId,
        to_account_id: request.toAccountId,
        transfer_id: transferId,
        type: 'transfer',
        amount: -Math.abs(request.amount), // Always negative for outgoing
        currency: fromAccount.currency,
        exchange_rate: exchangeRate,
        date: request.date,
        description: request.description || '',
        notes: request.notes || '',
        status: 'completed',
        tags: request.tags || [],
        created_at: now,
        updated_at: now,
        metadata: {
          isTransfer: true,
          fromAccountId: request.fromAccountId,
          toAccountId: request.toAccountId,
          fromAccountName: fromAccount.name,
          toAccountName: toAccount.name,
          fromCurrencyCode: fromAccount.currency,
          toCurrencyCode: toAccount.currency,
          direction: 'out'
        }
      };

      // Create incoming transaction (income)
      const incomingTransaction: Partial<Transaction> = {
        id: uuidv4(),
        user_id: userId,
        account_id: request.toAccountId,
        to_account_id: request.fromAccountId,
        transfer_id: transferId,
        type: 'transfer',
        amount: Math.abs(convertedAmount), // Always positive for incoming
        currency: toAccount.currency,
        exchange_rate: 1 / exchangeRate, // Inverse rate for the receiving side
        date: request.date,
        description: request.description || '',
        notes: request.notes || '',
        status: 'completed',
        tags: request.tags || [],
        created_at: now,
        updated_at: now,
        metadata: {
          isTransfer: true,
          fromAccountId: request.fromAccountId,
          toAccountId: request.toAccountId,
          fromAccountName: fromAccount.name,
          toAccountName: toAccount.name,
          fromCurrencyCode: fromAccount.currency,
          toCurrencyCode: toAccount.currency,
          direction: 'in'
        }
      };

      // Insert both transactions in a transaction block
      const { data: outgoingResult, error: outgoingError } = await supabase
        .from('transactions')
        .insert([outgoingTransaction])
        .select()
        .single();

      if (outgoingError) throw outgoingError;

      const { data: incomingResult, error: incomingError } = await supabase
        .from('transactions')
        .insert([incomingTransaction])
        .select()
        .single();

      if (incomingError) {
        // Rollback outgoing transaction if incoming fails
        await supabase.from('transactions').delete().eq('id', outgoingResult.id);
        throw incomingError;
      }

      // Return the transfer object
      return {
        id: transferId,
        fromAccountId: request.fromAccountId,
        toAccountId: request.toAccountId,
        amount: request.amount,
        exchangeRate,
        date: request.date,
        description: request.description || '',
        notes: request.notes,
        status: 'completed',
        tags: request.tags,
        fromTransaction: outgoingResult as Transaction,
        toTransaction: incomingResult as Transaction,
        fromCurrency: fromAccount.currency,
        toCurrency: toAccount.currency,
        convertedAmount,
        created_at: now,
        updated_at: now
      };

    } catch (error) {
      console.error('Error creating transfer:', error);
      throw error;
    }
  }

  /**
   * Update an existing transfer transaction
   */
  async updateTransfer(userId: string, transferId: string, updates: UpdateTransferRequest): Promise<TransferTransaction> {
    try {
      // Get existing transfer transactions
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('transfer_id', transferId)
        .eq('user_id', userId);

      if (error) throw error;
      if (!transactions || transactions.length !== 2) {
        throw new Error('Transfer not found or incomplete');
      }

      // Identify outgoing and incoming transactions
      const outgoingTransaction = transactions.find(t => t.amount < 0);
      const incomingTransaction = transactions.find(t => t.amount > 0);

      if (!outgoingTransaction || !incomingTransaction) {
        throw new Error('Invalid transfer structure');
      }

      // Get account information if we need to recalculate amounts
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, name, currency')
        .in('id', [outgoingTransaction.account_id, incomingTransaction.account_id]);

      const fromAccount = accounts?.find(a => a.id === outgoingTransaction.account_id);
      const toAccount = accounts?.find(a => a.id === incomingTransaction.account_id);

      if (!fromAccount || !toAccount) {
        throw new Error('Could not find transfer accounts');
      }

      // Calculate new values
      const newAmount = updates.amount !== undefined ? updates.amount : Math.abs(outgoingTransaction.amount);
      const newExchangeRate = updates.exchangeRate !== undefined ? updates.exchangeRate : (outgoingTransaction.exchange_rate || 1.0);
      const newConvertedAmount = newAmount * newExchangeRate;
      const now = new Date().toISOString();

      // Prepare update data
      const updateData = {
        amount: updates.amount !== undefined ? -Math.abs(newAmount) : undefined, // Keep negative for outgoing
        exchange_rate: newExchangeRate,
        date: updates.date,
        description: updates.description,
        notes: updates.notes,
        tags: updates.tags,
        updated_at: now
      };

      const incomingUpdateData = {
        amount: updates.amount !== undefined ? Math.abs(newConvertedAmount) : undefined, // Keep positive for incoming
        exchange_rate: 1 / newExchangeRate,
        date: updates.date,
        description: updates.description,
        notes: updates.notes,
        tags: updates.tags,
        updated_at: now
      };

      // Remove undefined values safely
      const cleanUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      ) as Partial<Transaction>;
      
      const cleanIncomingUpdateData = Object.fromEntries(
        Object.entries(incomingUpdateData).filter(([_, value]) => value !== undefined)
      ) as Partial<Transaction>;

      // Update both transactions
      const { error: outgoingUpdateError } = await supabase
        .from('transactions')
        .update(cleanUpdateData)
        .eq('id', outgoingTransaction.id);

      if (outgoingUpdateError) throw outgoingUpdateError;

      const { error: incomingUpdateError } = await supabase
        .from('transactions')
        .update(cleanIncomingUpdateData)
        .eq('id', incomingTransaction.id);

      if (incomingUpdateError) throw incomingUpdateError;

      // Return updated transfer
      return await this.getTransferById(userId, transferId);

    } catch (error) {
      console.error('Error updating transfer:', error);
      throw error;
    }
  }

  /**
   * Get a transfer by its ID
   */
  async getTransferById(userId: string, transferId: string): Promise<TransferTransaction> {
    try {
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('transfer_id', transferId)
        .eq('user_id', userId);

      if (error) throw error;
      if (!transactions || transactions.length !== 2) {
        throw new Error('Transfer not found or incomplete');
      }

      const outgoingTransaction = transactions.find(t => t.amount < 0);
      const incomingTransaction = transactions.find(t => t.amount > 0);

      if (!outgoingTransaction || !incomingTransaction) {
        throw new Error('Invalid transfer structure');
      }

      // Get account information
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, name, currency')
        .in('id', [outgoingTransaction.account_id, incomingTransaction.account_id]);

      const fromAccount = accounts?.find(a => a.id === outgoingTransaction.account_id);
      const toAccount = accounts?.find(a => a.id === incomingTransaction.account_id);

      return {
        id: transferId,
        fromAccountId: outgoingTransaction.account_id,
        toAccountId: incomingTransaction.account_id,
        amount: Math.abs(outgoingTransaction.amount),
        exchangeRate: outgoingTransaction.exchange_rate || 1.0,
        date: outgoingTransaction.date,
        description: outgoingTransaction.description || '',
        notes: outgoingTransaction.notes,
        status: outgoingTransaction.status as 'pending' | 'completed' | 'cancelled',
        tags: outgoingTransaction.tags,
        fromTransaction: outgoingTransaction as Transaction,
        toTransaction: incomingTransaction as Transaction,
        fromCurrency: fromAccount?.currency || 'USD',
        toCurrency: toAccount?.currency || 'USD',
        convertedAmount: Math.abs(incomingTransaction.amount),
        created_at: outgoingTransaction.created_at,
        updated_at: outgoingTransaction.updated_at
      };

    } catch (error) {
      console.error('Error getting transfer:', error);
      throw error;
    }
  }

  /**
   * Get a transfer by transaction ID (either side of the transfer)
   */
  async getTransferByTransactionId(userId: string, transactionId: string): Promise<TransferTransaction | null> {
    try {
      const { data: transaction, error } = await supabase
        .from('transactions')
        .select('transfer_id')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (error || !transaction?.transfer_id) {
        return null;
      }

      return await this.getTransferById(userId, transaction.transfer_id);

    } catch (error) {
      console.error('Error getting transfer by transaction ID:', error);
      return null;
    }
  }

  /**
   * Delete a transfer (both transactions)
   */
  async deleteTransfer(userId: string, transferId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('transactions')
        .delete()
        .eq('transfer_id', transferId)
        .eq('user_id', userId);

      if (error) throw error;

    } catch (error) {
      console.error('Error deleting transfer:', error);
      throw error;
    }
  }

  /**
   * Get current exchange rate between two currencies
   */
  private async getCurrentExchangeRate(fromCurrency: string, toCurrency: string): Promise<number> {
    try {
      // First check if we have a recent rate in the database
      const { data: rate } = await supabase
        .from('exchange_rates')
        .select('rate')
        .eq('base_currency', fromCurrency)
        .eq('target_currency', toCurrency)
        .gte('timestamp', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Within 24 hours
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();

      if (rate) {
        return rate.rate;
      }

      // If no recent rate found, you could implement API call to exchange rate service
      // For now, return 1.0 as fallback
      return 1.0;

    } catch (error) {
      console.error('Error getting exchange rate:', error);
      return 1.0;
    }
  }

  /**
   * Check if a transaction is part of a transfer
   */
  async isTransferTransaction(userId: string, transactionId: string): Promise<boolean> {
    try {
      const { data: transaction, error } = await supabase
        .from('transactions')
        .select('transfer_id, type')
        .eq('id', transactionId)
        .eq('user_id', userId)
        .single();

      if (error) return false;

      return !!(transaction?.transfer_id || transaction?.type === 'transfer');

    } catch (error) {
      return false;
    }
  }

  /**
   * Convert two existing transactions into a transfer
   */
  async convertToTransfer(
    userId: string, 
    sourceTransaction: Transaction, 
    targetTransaction: Transaction
  ): Promise<TransferTransaction> {
    try {
      // Generate a unique transfer ID to link both transactions
      const transferId = uuidv4();
      const now = new Date().toISOString();

      // Determine which is the source (expense) and which is target (income)
      const isSourceExpense = sourceTransaction.amount < 0 || sourceTransaction.type === 'expense';
      const fromTransaction = isSourceExpense ? sourceTransaction : targetTransaction;
      const toTransaction = isSourceExpense ? targetTransaction : sourceTransaction;

      // Get account information for currency and names
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, name, currency')
        .in('id', [fromTransaction.account_id, toTransaction.account_id])
        .eq('user_id', userId);

      if (!accounts || accounts.length !== 2) {
        throw new Error('Could not find both accounts');
      }

      const fromAccount = accounts.find(a => a.id === fromTransaction.account_id);
      const toAccount = accounts.find(a => a.id === toTransaction.account_id);

      if (!fromAccount || !toAccount) {
        throw new Error('Could not find specified accounts');
      }

      // Calculate exchange rate from existing transactions
      const fromAmount = Math.abs(fromTransaction.amount);
      const toAmount = Math.abs(toTransaction.amount);
      const exchangeRate = fromAmount > 0 ? toAmount / fromAmount : 1.0;

      // Prepare updates for both transactions
      const fromTransactionUpdate = {
        transfer_id: transferId,
        type: 'transfer' as const,
        amount: -Math.abs(fromAmount), // Always negative for outgoing
        to_account_id: toAccount.id,
        exchange_rate: exchangeRate,
        updated_at: now,
        metadata: {
          ...fromTransaction.metadata,
          isTransfer: true,
          fromAccountId: fromAccount.id,
          toAccountId: toAccount.id,
          fromAccountName: fromAccount.name,
          toAccountName: toAccount.name,
          fromCurrencyCode: fromAccount.currency,
          toCurrencyCode: toAccount.currency,
          direction: 'out'
        }
      };

      const toTransactionUpdate = {
        transfer_id: transferId,
        type: 'transfer' as const,
        amount: Math.abs(toAmount), // Always positive for incoming
        to_account_id: fromAccount.id,
        exchange_rate: 1 / exchangeRate, // Inverse rate for the receiving side
        updated_at: now,
        metadata: {
          ...toTransaction.metadata,
          isTransfer: true,
          fromAccountId: fromAccount.id,
          toAccountId: toAccount.id,
          fromAccountName: fromAccount.name,
          toAccountName: toAccount.name,
          fromCurrencyCode: fromAccount.currency,
          toCurrencyCode: toAccount.currency,
          direction: 'in'
        }
      };

      // Update both transactions
      const { data: updatedFromTransaction, error: fromError } = await supabase
        .from('transactions')
        .update(fromTransactionUpdate)
        .eq('id', fromTransaction.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (fromError) throw fromError;

      const { data: updatedToTransaction, error: toError } = await supabase
        .from('transactions')
        .update(toTransactionUpdate)
        .eq('id', toTransaction.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (toError) {
        // Rollback the first transaction if second fails
        await supabase
          .from('transactions')
          .update({
            transfer_id: null,
            type: fromTransaction.type,
            to_account_id: null,
            exchange_rate: null,
            metadata: fromTransaction.metadata || {}
          })
          .eq('id', fromTransaction.id);
        
        throw toError;
      }

      // Return the transfer object
      return {
        id: transferId,
        fromAccountId: fromAccount.id,
        toAccountId: toAccount.id,
        amount: fromAmount,
        exchangeRate,
        date: fromTransaction.date,
        description: fromTransaction.description || '',
        notes: fromTransaction.notes,
        status: 'completed',
        tags: fromTransaction.tags,
        fromTransaction: updatedFromTransaction as Transaction,
        toTransaction: updatedToTransaction as Transaction,
        fromCurrency: fromAccount.currency,
        toCurrency: toAccount.currency,
        convertedAmount: toAmount,
        created_at: fromTransaction.created_at,
        updated_at: now
      };

    } catch (error) {
      console.error('Error converting to transfer:', error);
      throw error;
    }
  }

  /**
   * Split a transfer into separate income and expense transactions
   */
  async splitTransfer(userId: string, transferId: string): Promise<{
    incomeTransaction: Transaction;
    expenseTransaction: Transaction;
  }> {
    try {
      // Get existing transfer transactions
      const { data: transactions, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('transfer_id', transferId)
        .eq('user_id', userId);

      if (error) throw error;
      if (!transactions || transactions.length !== 2) {
        throw new Error('Transfer not found or incomplete');
      }

      // Identify outgoing and incoming transactions
      const outgoingTransaction = transactions.find(t => t.amount < 0);
      const incomingTransaction = transactions.find(t => t.amount > 0);

      if (!outgoingTransaction || !incomingTransaction) {
        throw new Error('Invalid transfer structure');
      }

      const now = new Date().toISOString();

      // Convert outgoing transaction to expense
      const expenseUpdate = {
        transfer_id: null,
        to_account_id: null,
        type: 'expense' as const,
        exchange_rate: null,
        updated_at: now,
        metadata: {
          ...outgoingTransaction.metadata,
          wasTransfer: true,
          splitFromTransfer: transferId,
          originalTransferDate: now
        }
      };

      // Convert incoming transaction to income
      const incomeUpdate = {
        transfer_id: null,
        to_account_id: null,
        type: 'income' as const,
        exchange_rate: null,
        updated_at: now,
        metadata: {
          ...incomingTransaction.metadata,
          wasTransfer: true,
          splitFromTransfer: transferId,
          originalTransferDate: now
        }
      };

      // Clean up transfer-related metadata
      delete expenseUpdate.metadata.isTransfer;
      delete expenseUpdate.metadata.fromAccountId;
      delete expenseUpdate.metadata.toAccountId;
      delete expenseUpdate.metadata.fromAccountName;
      delete expenseUpdate.metadata.toAccountName;
      delete expenseUpdate.metadata.fromCurrencyCode;
      delete expenseUpdate.metadata.toCurrencyCode;
      delete expenseUpdate.metadata.direction;

      delete incomeUpdate.metadata.isTransfer;
      delete incomeUpdate.metadata.fromAccountId;
      delete incomeUpdate.metadata.toAccountId;
      delete incomeUpdate.metadata.fromAccountName;
      delete incomeUpdate.metadata.toAccountName;
      delete incomeUpdate.metadata.fromCurrencyCode;
      delete incomeUpdate.metadata.toCurrencyCode;
      delete incomeUpdate.metadata.direction;

      // Update both transactions
      const { data: updatedExpense, error: expenseError } = await supabase
        .from('transactions')
        .update(expenseUpdate)
        .eq('id', outgoingTransaction.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (expenseError) throw expenseError;

      const { data: updatedIncome, error: incomeError } = await supabase
        .from('transactions')
        .update(incomeUpdate)
        .eq('id', incomingTransaction.id)
        .eq('user_id', userId)
        .select()
        .single();

      if (incomeError) {
        // Rollback the expense transaction if income update fails
        await supabase
          .from('transactions')
          .update({
            transfer_id: transferId,
            to_account_id: incomingTransaction.account_id,
            type: 'transfer',
            exchange_rate: outgoingTransaction.exchange_rate,
            metadata: outgoingTransaction.metadata
          })
          .eq('id', outgoingTransaction.id);
        
        throw incomeError;
      }

      return {
        expenseTransaction: updatedExpense as Transaction,
        incomeTransaction: updatedIncome as Transaction
      };

    } catch (error) {
      console.error('Error splitting transfer:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const transferService = new TransferService(); 